/**
 * The public account-deletion page (X-03, W8-PRIVACY-WORKER, D439 — a Google Play requirement). Anyone can ask for their
 * Vello account to be deleted here, without the app: the number → "Enviar código" (the existing OTP send) → the code →
 * "Confirmar exclusão" (the public deletion request) → ONE confirmation.
 *
 * Honest by construction: before the request the page says what is deleted and what the law makes us keep; after it, the
 * confirmation is the same words whether or not the number had an account — the api answers the same 202 and this page
 * never reads its body (no enumeration). A wrong code is an inline error with a retry; too many attempts says so.
 *
 * Design rules: the site's own tokens/classes (every color a `@vello/tokens` variable), ONE primary action per step,
 * 48 px targets, a visible focus ring, no native dialog or native validation bubble (`noValidate`), and focus moves to
 * each new step's heading so a screen reader hears where it is. All copy comes from the site catalog (`t`).
 */
import { type FormEvent, type MouseEvent, type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import wordmarkInk from '@vello/brand/assets/wordmark/svg/vello-wordmark-ink.svg';
import { createAccountDeletionClient, normalizeOtpCode, normalizePhoneE164, type ConfirmOutcome, type SendCodeOutcome } from '../src/account-deletion.js';
import type { Copy } from './i18n';

type Step = { readonly name: 'phone' } | { readonly name: 'code'; readonly challengeId: string; readonly maskedDestination?: string } | { readonly name: 'done' };

export interface AccountDeletionPageProps {
  readonly t: Copy;
  /** The resolved display locale, sent with the OTP request so the code arrives in the visitor's language. */
  readonly locale: string;
  readonly apiBaseUrl: string;
  /** `+<calling code>` to prefill the phone field for the market, or '' (the visitor types the full number). */
  readonly phonePrefix: string;
  readonly homeHref: string;
  readonly onNavigate: (event: MouseEvent<HTMLAnchorElement>, href: string) => void;
}

/** The catalog message id for a refusal of the OTP send. */
function sendErrorId(outcome: Exclude<SendCodeOutcome, { kind: 'sent' }>): string {
  switch (outcome.kind) {
    case 'invalid-phone':
      return 'erase.phone.invalid';
    case 'rate-limited':
      return 'erase.error.rateLimited';
    case 'challenge-required':
      return 'erase.error.challenge';
    case 'not-served':
      return 'erase.error.notServed';
    case 'unavailable':
      return 'erase.error.unavailable';
  }
}

/** The catalog message id for a refusal of the deletion request. */
function confirmErrorId(outcome: Exclude<ConfirmOutcome, { kind: 'received' }>): string {
  switch (outcome.kind) {
    case 'wrong-code':
      return 'erase.code.wrong';
    case 'rate-limited':
      return 'erase.error.rateLimited';
    case 'unavailable':
      return 'erase.error.unavailable';
  }
}

const DELETED_ITEMS = ['erase.deleted.account', 'erase.deleted.sessions', 'erase.deleted.chat', 'erase.deleted.location', 'erase.deleted.saved'] as const;
const KEPT_ITEMS = ['erase.kept.records', 'erase.kept.safety'] as const;

export function AccountDeletionPage({ t, locale, apiBaseUrl, phonePrefix, homeHref, onNavigate }: AccountDeletionPageProps): ReactElement {
  const client = useMemo(() => createAccountDeletionClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);
  const [step, setStep] = useState<Step>({ name: 'phone' });
  const [phone, setPhone] = useState(phonePrefix);
  const [code, setCode] = useState('');
  const [errorId, setErrorId] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const stepHeading = useRef<HTMLHeadingElement>(null);
  const firstStep = useRef(true);

  // Move focus to each new step's heading (not on the first paint: the visitor starts at the top of the page).
  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false;
      return;
    }
    stepHeading.current?.focus();
  }, [step.name]);

  const onSendCode = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    const phoneE164 = normalizePhoneE164(phone);
    if (phoneE164 === undefined) {
      setErrorId('erase.phone.invalid');
      return;
    }
    setBusy(true);
    setErrorId(undefined);
    const outcome = await client.requestCode({ phoneE164, locale });
    setBusy(false);
    if (outcome.kind !== 'sent') {
      setErrorId(sendErrorId(outcome));
      return;
    }
    setCode('');
    setStep({ name: 'code', challengeId: outcome.challengeId, ...(outcome.maskedDestination !== undefined ? { maskedDestination: outcome.maskedDestination } : {}) });
  };

  const onConfirm = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (busy || step.name !== 'code') return;
    const typed = normalizeOtpCode(code);
    if (typed === '') {
      setErrorId('erase.code.empty');
      return;
    }
    setBusy(true);
    setErrorId(undefined);
    const outcome = await client.confirm({ challengeId: step.challengeId, code: typed });
    setBusy(false);
    if (outcome.kind !== 'received') {
      setErrorId(confirmErrorId(outcome));
      return;
    }
    setStep({ name: 'done' });
  };

  const onChangeNumber = (): void => {
    setErrorId(undefined);
    setCode('');
    setStep({ name: 'phone' });
  };

  const error = errorId !== undefined ? t(errorId) : undefined;

  return (
    <div className="bootstrap">
      <header className="nav">
        <div className="nav-inner">
          <a className="nav-home" href={homeHref} onClick={(e) => onNavigate(e, homeHref)} aria-label={t('site.footer.home')}>
            <img className="nav-wordmark" src={wordmarkInk} alt="Vello" />
          </a>
        </div>
      </header>

      <main className="erase">
        <h1 className="erase-title">{t('erase.title')}</h1>

        {step.name !== 'done' && (
          <>
            <p className="erase-lead">{t('erase.intro')}</p>
            <div className="erase-scope">
              <section className="erase-scope-col">
                <h2 id="erase-deleted-title" className="erase-scope-title">{t('erase.deleted.title')}</h2>
                <ul className="erase-list" aria-labelledby="erase-deleted-title">
                  {DELETED_ITEMS.map((id) => (<li key={id}>{t(id)}</li>))}
                </ul>
              </section>
              <section className="erase-scope-col">
                <h2 id="erase-kept-title" className="erase-scope-title">{t('erase.kept.title')}</h2>
                <ul className="erase-list" aria-labelledby="erase-kept-title">
                  {KEPT_ITEMS.map((id) => (<li key={id}>{t(id)}</li>))}
                </ul>
              </section>
            </div>
          </>
        )}

        {step.name === 'phone' && (
          <form className="erase-card" noValidate onSubmit={(e) => void onSendCode(e)}>
            <h2 className="erase-step-title" ref={stepHeading} tabIndex={-1}>{t('erase.phone.step')}</h2>
            <div className="field">
              <label className="field-label" htmlFor="erase-phone">{t('erase.phone.label')}</label>
              <input
                id="erase-phone"
                className="input tnum"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                spellCheck={false}
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  setErrorId(undefined);
                }}
                aria-invalid={error !== undefined}
                aria-describedby={error !== undefined ? 'erase-phone-hint erase-phone-error' : 'erase-phone-hint'}
              />
              <span id="erase-phone-hint" className="field-hint">{t('erase.phone.hint')}</span>
              {error !== undefined && (<p id="erase-phone-error" className="field-error" role="alert">{error}</p>)}
            </div>
            <div className="erase-actions">
              <button type="submit" className="btn btn--primary" disabled={busy} aria-busy={busy}>
                {busy ? t('erase.phone.sending') : t('erase.phone.submit')}
              </button>
            </div>
          </form>
        )}

        {step.name === 'code' && (
          <form className="erase-card" noValidate onSubmit={(e) => void onConfirm(e)}>
            <h2 className="erase-step-title" ref={stepHeading} tabIndex={-1}>{t('erase.code.step')}</h2>
            <p className="erase-note" aria-live="polite">
              {step.maskedDestination !== undefined ? t('erase.code.sentTo', { destination: step.maskedDestination }) : t('erase.code.sent')}
            </p>
            <div className="field">
              <label className="field-label" htmlFor="erase-code">{t('erase.code.label')}</label>
              <input
                id="erase-code"
                className="input tnum"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                spellCheck={false}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  setErrorId(undefined);
                }}
                aria-invalid={error !== undefined}
                {...(error !== undefined ? { 'aria-describedby': 'erase-code-error' } : {})}
              />
              {error !== undefined && (<p id="erase-code-error" className="field-error" role="alert">{error}</p>)}
            </div>
            <div className="erase-actions">
              <button type="submit" className="btn btn--primary" disabled={busy} aria-busy={busy}>
                {busy ? t('erase.code.confirming') : t('erase.code.submit')}
              </button>
              <button type="button" className="btn btn--secondary" onClick={onChangeNumber} disabled={busy}>
                {t('erase.code.change')}
              </button>
            </div>
          </form>
        )}

        {step.name === 'done' && (
          <section className="erase-card" role="status" aria-labelledby="erase-step-done">
            <h2 id="erase-step-done" className="erase-step-title" ref={stepHeading} tabIndex={-1}>{t('erase.done.title')}</h2>
            <p className="erase-lead">{t('erase.done.body')}</p>
            <div className="erase-actions">
              <a className="btn btn--secondary" href={homeHref} onClick={(e) => onNavigate(e, homeHref)}>{t('erase.done.home')}</a>
            </div>
          </section>
        )}
      </main>

      <footer className="bootstrap-footer">
        <span className="tnum">{t('site.footer.rights')}</span>
      </footer>
    </div>
  );
}
